/**
 * Custom Cursor - Pure GSAP Implementation
 *
 * A clean, simple custom cursor using only GSAP.
 * No library conflicts, full control over behavior.
 */

import gsap from 'gsap';

class CustomCursor {
	constructor(options = {}) {
		this.options = {
			size: 50,
			speed: 0.5,
			ease: 'power2.out',
			hideOnLeave: true,
			...options,
		};

		this.cursor = null;
		this.cursorInner = null;
		this.cursorText = null;
		this.isVisible = false; // Start as false since we initialize with opacity: 0
		this.currentState = null;

		// Mouse position
		this.mouse = { x: 0, y: 0 };
		this.pos = { x: 0, y: 0 };

		// Only initialize on desktop
		if (window.innerWidth > 1024) {
			this.init();
		}
	}

	init() {
		this.createCursor();
		this.bindEvents();
		this.animate();

		// Store globally
		window.cursor = this;
	}

	createCursor() {
		// Create cursor elements
		this.cursor = document.createElement('div');
		this.cursor.className = 'custom-cursor';

		this.cursorInner = document.createElement('div');
		this.cursorInner.className = 'custom-cursor__inner';

		this.cursorText = document.createElement('div');
		this.cursorText.className = 'custom-cursor__text';

		this.cursorInner.appendChild(this.cursorText);
		this.cursor.appendChild(this.cursorInner);
		document.body.appendChild(this.cursor);

		// Set initial position offscreen
		gsap.set(this.cursor, { xPercent: -50, yPercent: -50, opacity: 0 });
	}

	bindEvents() {
		// Mouse move
		document.addEventListener('mousemove', (e) => {
			this.mouse.x = e.clientX;
			this.mouse.y = e.clientY;

			if (!this.isVisible) {
				this.show();
			}
		});

		// Mouse enter/leave
		if (this.options.hideOnLeave) {
			document.addEventListener('mouseleave', () => this.hide());
			document.addEventListener('mouseenter', () => this.show());
		}

		// Hover states - using delegation for better performance
		document.addEventListener('mouseenter', (e) => {
			const target = e.target;

			// Check if target is an Element before calling closest
			if (target && target.closest) {
				const cursorElement = target.closest('[data-cursor]');
				if (cursorElement) {
					const state = cursorElement.dataset.cursor;
					const text = cursorElement.dataset.cursorText;
					this.setState(state, text);
				}
			}
		}, true);

		document.addEventListener('mouseleave', (e) => {
			const target = e.target;

			// Check if target is an Element before calling closest
			if (target && target.closest) {
				const cursorElement = target.closest('[data-cursor]');
				if (cursorElement) {
					this.removeState();
				}
			}
		}, true);

		// Mouse down/up for active state
		document.addEventListener('mousedown', () => {
			this.cursor.classList.add('is-active');
		});

		document.addEventListener('mouseup', () => {
			this.cursor.classList.remove('is-active');
		});
	}

	animate() {
		// Smooth follow animation
		this.pos.x += (this.mouse.x - this.pos.x) * this.options.speed;
		this.pos.y += (this.mouse.y - this.pos.y) * this.options.speed;

		gsap.set(this.cursor, {
			x: this.pos.x,
			y: this.pos.y,
		});

		requestAnimationFrame(() => this.animate());
	}

	setState(state, text = null) {
		// Remove all state classes
		this.cursor.className = 'custom-cursor';

		// Add new state
		if (state) {
			this.cursor.classList.add(`is-${state}`);
			this.currentState = state;
		}

		// Set text if provided
		if (text) {
			this.cursorText.textContent = text;
			this.cursor.classList.add('has-text');
		} else {
			this.cursorText.textContent = '';
			this.cursor.classList.remove('has-text');
		}
	}

	removeState() {
		this.cursor.className = 'custom-cursor';
		this.cursorText.textContent = '';
		this.currentState = null;
	}

	show() {
		if (!this.isVisible) {
			gsap.to(this.cursor, {
				opacity: 1,
				duration: 0.3,
				ease: this.options.ease,
			});
			this.isVisible = true;
		}
	}

	hide() {
		if (this.isVisible) {
			gsap.to(this.cursor, {
				opacity: 0,
				duration: 0.3,
				ease: this.options.ease,
			});
			this.isVisible = false;
		}
	}

	destroy() {
		if (this.cursor && this.cursor.parentNode) {
			this.cursor.parentNode.removeChild(this.cursor);
		}
	}
}

// Setup cursor states on elements
function setupCursorStates() {
	// Links (but not in portfolio cards)
	document.querySelectorAll('a:not(.portfolio-card a):not(.portfolio-card__heading a)').forEach(link => {
		if (!link.hasAttribute('data-cursor')) {
			link.setAttribute('data-cursor', 'link');
		}
	});

	// Portfolio cards - only set on container
	document.querySelectorAll('.portfolio-card').forEach(card => {
		if (!card.hasAttribute('data-cursor')) {
			card.setAttribute('data-cursor', 'pointer');
			card.setAttribute('data-cursor-text', 'View');
		}
	});

	// Gallery items
	document.querySelectorAll('.blocks-gallery-item:not(.portfolio-card):not(.portfolio-card *)').forEach(item => {
		item.setAttribute('data-cursor', 'pointer');
		item.setAttribute('data-cursor-text', 'View');
	});

	// Buttons (not magnetic)
	document.querySelectorAll('button:not(.magnetic)').forEach(button => {
		if (!button.hasAttribute('data-cursor')) {
			button.setAttribute('data-cursor', 'pointer');
		}
	});
}

// Auto-initialize
let customCursorInstance = null;

const initCustomCursor = () => {
	if (window.innerWidth > 1024) {
		setupCursorStates();
		customCursorInstance = new CustomCursor();
		window.customCursor = customCursorInstance;
	}
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCustomCursor);
} else {
	initCustomCursor();
}

export default CustomCursor;
